import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, PanelLeft, Search } from 'lucide-react';
import { useCommandPaletteStore } from '@/features/command-palette';
import { UserMenu, useAuthStore } from '@/features/auth';
import { NotificationBell } from '@/features/notifications';
import { StatusIndicator } from '@/features/system-status';
import { VersionBadge } from '@/features/changelog';
import { Burger, Tooltip } from '@/shared/ui';
import { useUiStore } from '@/shared/lib/uiStore';
import { useRevealStore } from '@/features/ai-assistant';
import { draftEntryPath, useDraftStore } from '@/features/tickets';
import { cn } from '@/shared/lib/cn';
import { BugReportButton } from './BugReportButton';
import { SupportLogo } from './SupportLogo';
import styles from './Header.module.css';

function useCreateButtonLabel() {
  const rightRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [withLabel, setWithLabel] = useState(true);
  const wideWidthRef = useRef(0);
  const narrowWidthRef = useRef(34);

  useLayoutEffect(() => {
    const el = rightRef.current;
    if (!el) return;
    const HYSTERESIS = 12;

    const check = () => {
      const node = rightRef.current;
      const button = buttonRef.current;
      if (!node || !button) return;
      if (labelRef.current) wideWidthRef.current = button.offsetWidth;
      else narrowWidthRef.current = button.offsetWidth;

      const gap = parseFloat(getComputedStyle(node).columnGap) || 0;
      const children = Array.from(node.children) as HTMLElement[];
      const content =
        children.reduce((sum, child) => sum + child.offsetWidth, 0) +
        gap * Math.max(0, children.length - 1);
      const free = node.clientWidth - content;
      const labelCost = wideWidthRef.current - narrowWidthRef.current;

      setWithLabel((prev) => {
        if (prev) return free >= 0;
        if (labelCost <= 0) return false;
        return free >= labelCost + HYSTERESIS;
      });
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  }, []);

  return { rightRef, buttonRef, labelRef, withLabel };
}

export function Header() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const setMobileNav = useUiStore((s) => s.setMobileNav);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const aiPortal = useRevealStore((s) => s.enabled);
  const { rightRef, buttonRef, labelRef, withLabel } = useCreateButtonLabel();

  const draft = useDraftStore((s) => s.draft);
  const fetchDraft = useDraftStore((s) => s.fetchDraft);
  useEffect(() => {
    if (isAuthenticated) void fetchDraft();
  }, [isAuthenticated, fetchDraft]);

  const handleToggle = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches) {
      setMobileNav(!mobileNavOpen);
    } else {
      toggleSidebar();
    }
  };

  return (
    <header
      className={cn(styles.wrapper, aiPortal && styles.wrapperGlass, !isAuthenticated && styles.wrapperGuest)}
    >
      <div className={styles.start}>
        { }
        {isAuthenticated && (
          <Link to="/" className={styles.logo} aria-label="Support — на главную">
            <SupportLogo className={styles.logoSvg} />
          </Link>
        )}
        {isAuthenticated && <StatusIndicator />}
        { }
        <VersionBadge />
        {isAuthenticated && (
          <Tooltip content={collapsed ? 'Развернуть меню' : 'Свернуть меню'} side="bottom">
            <button
              type="button"
              className={cn(styles.menuButton, collapsed && styles.menuButtonPulse)}
              onClick={handleToggle}
              aria-label={mobileNavOpen ? 'Закрыть меню' : 'Открыть меню'}
              aria-expanded={mobileNavOpen}
              data-tour="menu-button"
            >
              { }
              <PanelLeft size={15} aria-hidden className={styles.desktopIcon} />
              { }
              <Burger open={mobileNavOpen} className={styles.burgerMobile} />
            </button>
          </Tooltip>
        )}
      </div>
      <div className={styles.right} ref={rightRef}>
        { }
        {isAuthenticated && (
          <>
            <Tooltip
              content={draft ? 'Продолжить незаконченную заявку' : 'Новое обращение'}
              side="bottom"
            >
              <Link
                ref={buttonRef}
                to={draftEntryPath(draft)}
                className={cn(styles.createBtn, withLabel && styles.createBtnWide)}
                aria-label="Новое обращение"
                data-tour="create-ticket"
              >
                <Plus size={16} aria-hidden />
                {withLabel && (
                  <span ref={labelRef} className={styles.createBtnLabel}>
                    Обращение
                  </span>
                )}
              </Link>
            </Tooltip>
            <Tooltip content="Поиск и действия (Ctrl+K)" side="bottom">
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => useCommandPaletteStore.getState().setOpen(true)}
                aria-label="Поиск и действия"
                data-tour="search"
              >
                <Search size={17} aria-hidden />
              </button>
            </Tooltip>
            <BugReportButton />
            <NotificationBell />
            { }
            <span className={styles.userMenuDesktop} data-tour="user-menu">
              <UserMenu />
            </span>
          </>
        )}
      </div>
    </header>
  );
}
